import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ExpandSingle, formatCurrency, Header3, Tooltip } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { getFormatCurrencyArgs } from "~/store/selectors/selector.currencies";

export const ExpenseTotal = ({
  loggedUserTotal,
  othersUserTotal,
  hideExpandSingle,
  currencyCode,
  onChangeExpand,
  className,
}) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const formatterArgs = useSelector((state) => getFormatCurrencyArgs(state, currencyCode));

  const handleClick = useCallback(() => {
    const newShow = !show;
    if (typeof onChangeExpand === "function") {
      onChangeExpand(newShow);
    }
    setShow(newShow);
  }, [onChangeExpand, show]);

  return (
    <div
      className={classnames(className, "tw-flex", "tw-items-center", {
        "tw-cursor-pointer tw-hidden sm:tw-flex": !hideExpandSingle,
      })}
      onClick={handleClick}
    >
      {!hideExpandSingle && (
        <ExpandSingle
          className="tw-text-primary tw-mr-1"
          expanded={show}
        />
      )}
      {loggedUserTotal > 0 && !othersUserTotal ? (
        <Tooltip
          title={formatCurrency(
            loggedUserTotal,
            formatterArgs.thousand_separator,
            formatterArgs.decimal_separator,
            true,
            formatterArgs.decimal_spaces,
            "long",
            formatterArgs.code
          )}
        >
          <Header3
            tabularNums
            align="left"
            color="theme-secondary"
            weight="bold"
          >
            {t("Total")}:{" "}
            {formatCurrency(
              loggedUserTotal,
              formatterArgs.thousand_separator,
              formatterArgs.decimal_separator,
              true,
              formatterArgs.decimal_spaces,
              "short",
              formatterArgs.code
            )}
          </Header3>
        </Tooltip>
      ) : null}
      {loggedUserTotal > 0 && othersUserTotal > 0 ? (
        <Tooltip
          title={formatCurrency(
            loggedUserTotal,
            formatterArgs.thousand_separator,
            formatterArgs.decimal_separator,
            false,
            formatterArgs.decimal_spaces,
            "long",
            formatterArgs.code
          )}
        >
          <Header3
            tabularNums
            align="left"
            color="theme-secondary"
            weight="bold"
            className="tw-mr-2"
          >
            {t("You")}:{" "}
            {formatCurrency(
              loggedUserTotal,
              formatterArgs.thousand_separator,
              formatterArgs.decimal_separator,
              true,
              formatterArgs.decimal_spaces,
              "short",
              formatterArgs.code
            )}
            ,
          </Header3>
        </Tooltip>
      ) : null}
      {othersUserTotal > 0 ? (
        <Tooltip
          title={formatCurrency(
            othersUserTotal,
            formatterArgs.thousand_separator,
            formatterArgs.decimal_separator,
            false,
            formatterArgs.decimal_spaces,
            "long",
            formatterArgs.code
          )}
        >
          <Header3
            tabularNums
            align="right"
            color="theme-secondary"
          >
            {t("Others")}:{" "}
            {formatCurrency(
              othersUserTotal,
              formatterArgs.thousand_separator,
              formatterArgs.decimal_separator,
              true,
              formatterArgs.decimal_spaces,
              "short",
              formatterArgs.code
            )}
          </Header3>
        </Tooltip>
      ) : null}
    </div>
  );
};

ExpenseTotal.propTypes = {
  loggedUserTotal: PropTypes.number,
  othersUserTotal: PropTypes.number,
  hideExpandSingle: PropTypes.bool,
  currencyCode: PropTypes.string.isRequired,
  onChangeExpand: PropTypes.func,
  className: PropTypes.string,
};

ExpenseTotal.defaultProps = {
  hideExpandSingle: false,
  loggedUserTotal: 0,
  othersUserTotal: 0,
};
